import React from "react";

import LoadingSpinner from "components/LoadingSpinner";
import useGetAccessToken from "components/Login/useGetAccessToken";
import ImportProgressOverlay from "components/Modal/ImportProgressOverlay";
import modalStyles from "components/Modal/Modal.module.scss";
import type {
  ImportProgress,
  ImportResult,
} from "services/dbApi/utilities/exportImport";
import { PRODUCT_NAME } from "utilities/productName";
import { TEAMS_SUPPORT_CHANNEL_URL } from "utilities/supportUrl";
import {
  downloadLegacyExport,
  readLegacyIdb,
  type LegacyIdbSnapshot,
} from "./idbLegacy";
import { migrateFromLegacyIdb } from "./migrateFromLegacyIdb";
import {
  IDB_MIGRATION_COMPLETED_KEY,
  IDB_MIGRATION_DISMISSED_KEY,
} from "./flags";

type Phase =
  | "checking"
  | "passthrough"
  | "intro"
  | "exported"
  | "uploading"
  | "uploadDone"
  | "skipReminder";

interface IdbMigrationProps {
  children: React.ReactNode;
}

/**
 * One-time gate that helps returning users move their pre-dbApi chat data
 * (stored in IndexedDB) up to the dbApi. Mounts before App so Suspense
 * queries don't fire until the user resolves the gate.
 *
 * After the user finishes the flow (Migrate or Skip), a localStorage flag
 * is set so the gate is a no-op on subsequent loads.
 */
export default function IdbMigration({ children }: IdbMigrationProps) {
  const [phase, setPhase] = React.useState<Phase>("checking");
  const [snapshot, setSnapshot] = React.useState<LegacyIdbSnapshot | null>(
    null
  );
  const [progress, setProgress] = React.useState<ImportProgress | null>(null);
  const [result, setResult] = React.useState<ImportResult | null>(null);
  // Flips to true if `handleExportChats` throws. The Intro footer surfaces
  // a Skip button when this is set so the user is never trapped on Intro
  // by an unrecoverable export failure.
  const [exportFailed, setExportFailed] = React.useState(false);
  const getAccessToken = useGetAccessToken();

  // Mount-time check: if the dismissed flag is set, or there's nothing in
  // IDB, skip straight to passthrough. Otherwise, present the intro screen.
  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (localStorage.getItem(IDB_MIGRATION_DISMISSED_KEY) === "true") {
        if (!cancelled) setPhase("passthrough");
        return;
      }
      const legacySnapshot = await readLegacyIdb();
      if (cancelled) return;
      if (legacySnapshot === null) {
        // Nothing to migrate. Set both flags so we never re-check and the
        // Settings → Migrate IDB button stays hidden.
        localStorage.setItem(IDB_MIGRATION_DISMISSED_KEY, "true");
        localStorage.setItem(IDB_MIGRATION_COMPLETED_KEY, "true");
        setPhase("passthrough");
        return;
      }
      setSnapshot(legacySnapshot);
      setPhase("intro");
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleExportChats = () => {
    if (!snapshot) return;
    try {
      downloadLegacyExport(snapshot);
    } catch (error) {
      console.error("Legacy export download failed:", error);
      alert(
        "Export failed. Your IndexedDB data is unchanged. You can still skip the backup and continue, or click Export Chats again to retry."
      );
      setExportFailed(true);
      return;
    }
    setPhase("exported");
  };

  const handleMigrate = async () => {
    if (!snapshot) return;
    setProgress({
      total: 0,
      imported: 0,
      skipped: 0,
      failed: 0,
      currentThreadName: "",
    });
    setPhase("uploading");
    try {
      const importResult = await migrateFromLegacyIdb(
        getAccessToken,
        setProgress
      );
      if (importResult.failed === 0) {
        localStorage.setItem(IDB_MIGRATION_COMPLETED_KEY, "true");
      }
      setResult(importResult);
      setPhase("uploadDone");
    } catch (error) {
      console.error("Migration upload failed:", error);
      alert(
        "Migration failed. You can retry by reloading the page; your IndexedDB data is unchanged."
      );
      setProgress(null);
      setResult(null);
      setPhase("exported");
    }
  };

  const handleSkip = () => {
    setPhase("skipReminder");
  };

  // Close-out for both `uploadDone` and `skipReminder`. Sets the dismissed
  // flag (gate visibility only) and unblocks the children. The completed
  // flag is set in `handleMigrate` on a clean run; Skip leaves it unset so
  // the user can finish the migration later via Settings → Migrate IDB.
  const handleFinish = () => {
    localStorage.setItem(IDB_MIGRATION_DISMISSED_KEY, "true");
    setProgress(null);
    setResult(null);
    setPhase("passthrough");
  };

  if (phase === "passthrough") {
    return <>{children}</>;
  }

  if (phase === "checking") {
    return <LoadingSpinner message="Checking for legacy data…" />;
  }

  if (phase === "uploading" || phase === "uploadDone") {
    if (progress === null) return null;
    return (
      <ImportProgressOverlay
        progress={progress}
        result={phase === "uploadDone" ? result ?? undefined : undefined}
        onClose={handleFinish}
      />
    );
  }

  // Card UI for intro / exported / skipReminder
  let header: string;
  let body: React.ReactNode;
  let footer: React.ReactNode;

  if (phase === "intro") {
    const threadCount = snapshot ? Object.keys(snapshot.chatThreads).length : 0;
    header = "Your chats are moving to the cloud";
    body = (
      <>
        <p>
          {PRODUCT_NAME} now stores your conversations in a backend database
          instead of your browser. We found{" "}
          <strong>
            {threadCount} chat{threadCount === 1 ? "" : "s"}
          </strong>{" "}
          in this browser's local storage.
        </p>
        <p>
          Before migrating, please export your chats to a JSON file as a
          personal backup in case anything happens to this browser before you
          finish migrating. Keep this tab open and don't reload the page until
          you finish.
        </p>
        Need help? Contact our{" "}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={TEAMS_SUPPORT_CHANNEL_URL}
          className={modalStyles.supportLink}
        >
          teams support channel
        </a>
        .
      </>
    );
    footer = (
      <>
        {exportFailed && (
          <button className={modalStyles.secondaryButton} onClick={handleSkip}>
            Skip
          </button>
        )}
        <button className={modalStyles.button} onClick={handleExportChats}>
          Export Chats
        </button>
      </>
    );
  } else if (phase === "exported") {
    header = "Backup downloaded";
    body = (
      <>
        <p>
          Your chats have been exported to your downloads folder. Now choose how
          to proceed:
        </p>
        <ul>
          <li>
            <strong>Migrate all chats</strong> — uploads your local chats to the
            database. This may take a while if you have many chats.
          </li>
          <li>
            <strong>Skip (start fresh)</strong> — proceeds without uploading.
            Your local data stays in IndexedDB; you can migrate it later via
            Settings → Migrate IDB.
          </li>
        </ul>
        Need help? Contact our{" "}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={TEAMS_SUPPORT_CHANNEL_URL}
          className={modalStyles.supportLink}
        >
          teams support channel
        </a>
        .
      </>
    );
    footer = (
      <>
        <button className={modalStyles.secondaryButton} onClick={handleSkip}>
          Skip (start fresh)
        </button>
        <button className={modalStyles.button} onClick={handleMigrate}>
          Migrate all chats
        </button>
      </>
    );
  } else {
    // skipReminder
    header = "Saved for later";
    body = exportFailed ? (
      <p>
        Your chats stayed in this browser's IndexedDB and weren't uploaded.
        Because the backup export failed, you don't have a local JSON copy. To
        migrate your chats later, open the settings menu in the sidebar and
        click <strong>Migrate IDB</strong>.
      </p>
    ) : (
      <p>
        No chats were uploaded. If you'd like to bring them in later, open the
        settings menu in the sidebar and click <strong>Migrate IDB</strong>.
        Your downloaded JSON file is your personal backup in case anything
        happens to this browser before then.
      </p>
    );
    footer = (
      <button className={modalStyles.button} onClick={handleFinish}>
        Continue
      </button>
    );
  }

  return (
    <div className={modalStyles.backdrop}>
      <div className={modalStyles.modal}>
        <div className={modalStyles.header}>
          <h2 className={modalStyles.sectionTitle}>{header}</h2>
        </div>
        <div className={modalStyles.content}>{body}</div>
        <div className={modalStyles.footer}>{footer}</div>
      </div>
    </div>
  );
}
